import { XIcon } from '@phosphor-icons/react';
import type { ReactNode } from 'react';
import {
  Modal as AriaModal,
  Dialog,
  Heading,
  ModalOverlay,
} from 'react-aria-components';
import { Button } from './Button';
import { cn } from './lib/cn';

export type DrawerSize = 'md' | 'lg' | 'full';

export type DrawerProps = {
  isOpen: boolean;
  onOpenChange: (isOpen: boolean) => void;
  children: ReactNode | ((opts: { close: () => void }) => ReactNode);
  /** Can hold a control, such as a Select beside the heading. */
  title?: ReactNode;
  /** Pinned along the bottom, separated by a rule: the panel's actions. */
  footer?: ReactNode;
  /** `md` for edit forms, `lg` for pickers with a table, `full` for timelines. */
  size?: DrawerSize;
  /**
   * Set false while an action is in flight, to keep Esc, the backdrop and the
   * close button from closing it.
   */
  isDismissable?: boolean;
  'aria-label'?: string;
  className?: string;
};

/* Each is capped by the overlay's padding, so none overflows a narrow screen. */
const SIZES: Record<DrawerSize, string> = {
  md: 'sk:w-120',
  lg: 'sk:w-300',
  /* Leaves a strip of the page showing on the left, so it still reads as a panel. */
  full: 'sk:w-[calc(100%-56px)]',
};

/*
 * React Aria keeps the panel mounted until its transitions end, so the same
 * classes run the exit. With reduced motion the slide becomes a fade.
 */
const MOTION = cn(
  'sk:transition-[translate,opacity] sk:duration-200 sk:ease-out',
  'sk:motion-safe:rac-entering:translate-x-full sk:motion-safe:rac-exiting:translate-x-full',
  'sk:motion-reduce:rac-entering:opacity-0 sk:motion-reduce:rac-exiting:opacity-0',
);

/**
 * Focus-trapping panel from the right edge, for detail and edit work that
 * needs more room than a Modal. The header and footer stay put; the body scrolls.
 */
export function Drawer({
  isOpen,
  onOpenChange,
  children,
  title,
  footer,
  size = 'md',
  isDismissable = true,
  'aria-label': ariaLabel,
  className,
}: DrawerProps) {
  return (
    <ModalOverlay
      isOpen={isOpen}
      onOpenChange={onOpenChange}
      isDismissable={isDismissable}
      isKeyboardDismissDisabled={!isDismissable}
      className={cn(
        'sk:fixed sk:inset-0 sk:z-3000 sk:flex sk:justify-end sk:p-2',
        'sk:bg-black/25',
        'sk:transition-opacity sk:duration-200 sk:ease-out',
        'sk:rac-entering:opacity-0 sk:rac-exiting:opacity-0',
      )}
    >
      <AriaModal
        className={cn(
          'sk:flex sk:h-full sk:max-w-full sk:flex-col sk:overflow-hidden',
          'sk:rounded-panel sk:inset-shadow-hairline',
          'sk:bg-surface sk:shadow-card sk:outline-none',
          MOTION,
          SIZES[size],
          className,
        )}
      >
        <Dialog
          aria-label={title ? undefined : ariaLabel}
          className="sk:flex sk:min-h-0 sk:flex-1 sk:flex-col sk:outline-none"
        >
          {({ close }) => (
            <>
              <header
                className={cn(
                  'sk:flex sk:shrink-0 sk:items-center sk:gap-2 sk:px-4 sk:py-3',
                  title ? 'sk:border-b sk:border-rule' : 'sk:justify-end',
                )}
              >
                {title ? (
                  <Heading
                    slot="title"
                    className="sk:m-0 sk:flex sk:min-w-0 sk:flex-1 sk:items-center sk:gap-2 sk:text-sm sk:font-semibold sk:text-fg"
                  >
                    {title}
                  </Heading>
                ) : null}
                <Button
                  variant="ghost"
                  size="sm"
                  shape="circle"
                  aria-label="Close"
                  className="sk:shrink-0"
                  isDisabled={!isDismissable}
                  onPress={close}
                >
                  <XIcon size={12} weight="bold" aria-hidden />
                </Button>
              </header>

              <div className="sk:min-h-0 sk:flex-1 sk:overflow-y-auto sk:p-4 sk:text-dense sk:text-fg">
                {typeof children === 'function'
                  ? children({ close })
                  : children}
              </div>

              {footer ? (
                <footer className="sk:flex sk:shrink-0 sk:flex-wrap sk:items-center sk:justify-end sk:gap-2 sk:border-t sk:border-rule sk:px-4 sk:py-3">
                  {footer}
                </footer>
              ) : null}
            </>
          )}
        </Dialog>
      </AriaModal>
    </ModalOverlay>
  );
}
